import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { IconBadge } from '../components/icons/ServiceIcons';
import './ServicePage.css';
const dataEngineeringArtwork = '/assets/illustrations/data-engineering-pipeline.svg';

const DataEngineering = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [processRef, processInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const features = [
    {
      icon: 'integration',
      title: 'Unified Data Pipelines',
      description: 'Design streaming and batch pipelines with resilient recovery patterns, lineage, and data contracts.'
    },
    {
      icon: 'dataStack',
      title: 'Modern Warehouse & Lakehouse',
      description: 'Engineer scalable architectures on Snowflake, Databricks, BigQuery, or Redshift with governance and security baked in.'
    },
    {
      icon: 'governance',
      title: 'Data Trust & Quality',
      description: 'Embed quality scoring, lineage, catalogues, and stewardship workflows so decisions are grounded in trusted data.'
    },
    {
      icon: 'automation',
      title: 'Automation & MLOps Readiness',
      description: 'Package reusable components, data CI/CD, and orchestrated guardrails to accelerate analytics and ML delivery.'
    },
    {
      icon: 'api',
      title: 'Integration & API Fabrics',
      description: 'Connect SaaS, on-premise, and partner ecosystems through event-driven and API-first patterns.'
    },
    {
      icon: 'dashboard',
      title: 'Operational Dashboards',
      description: 'Real-time observability of pipeline health, spend, and SLAs for proactive platform operations.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Landscape Assessment',
      description: 'Catalogue data sources, integration footprints, and governance maturity to prioritise transformation.',
      icon: 'assessment'
    },
    {
      step: '02',
      title: 'Target Architecture',
      description: 'Design future-state reference architecture, security model, and phased implementation roadmap.',
      icon: 'architecture'
    },
    {
      step: '03',
      title: 'Engineering Sprints',
      description: 'Build modular pipelines, storage layers, and automations with automated testing and documentation.',
      icon: 'toolkit'
    },
    {
      step: '04',
      title: 'Enablement & Hand-off',
      description: 'Upskill internal teams with playbooks, runbooks, and co-delivery sessions.',
      icon: 'people'
    },
    {
      step: '05',
      title: 'Operate & Optimise',
      description: 'Provide managed services, FinOps insights, and enhancement backlogs to keep platforms evolving.',
      icon: 'analytics'
    }
  ];

  const technologies = [
    {
      name: 'Snowflake',
      tagline: 'Cloud Data Warehouse',
      description: 'Elastic compute, secure data sharing, and governance for enterprise analytics.',
      icon: 'dataStack'
    },
    {
      name: 'Databricks Lakehouse',
      tagline: 'Unified Analytics',
      description: 'Delta Lake, MLflow, and collaborative notebooks powering AI-ready pipelines.',
      icon: 'spark'
    },
    {
      name: 'Google BigQuery',
      tagline: 'Serverless Analytics',
      description: 'Highly parallel SQL engine with built-in ML and real-time streaming ingestion.',
      icon: 'dataStack'
    },
    {
      name: 'Amazon Redshift',
      tagline: 'Modern Warehouse',
      description: 'MPP architecture with RA3, Spectrum, and data sharing across AWS ecosystems.',
      icon: 'dataStack'
    },
    {
      name: 'Azure Synapse Analytics',
      tagline: 'Unified Lakehouse',
      description: 'Integrates dedicated SQL, Spark, and pipelines with Power BI and Purview.',
      icon: 'multiCloud'
    },
    {
      name: 'Apache Kafka & Confluent',
      tagline: 'Streaming Backbone',
      description: 'Event streaming, schema registry, and connectors for real-time data flows.',
      icon: 'realtime'
    },
    {
      name: 'Apache Flink & Spark Streaming',
      tagline: 'Stream Processing',
      description: 'Low-latency processing, CEP, and stateful analytics for IoT and telemetry.',
      icon: 'spark'
    },
    {
      name: 'Apache Airflow & Prefect',
      tagline: 'Workflow Orchestration',
      description: 'DAG-based scheduling, observability, and deployment automation for pipelines.',
      icon: 'automation'
    },
    {
      name: 'dbt Core & Cloud',
      tagline: 'Transformations',
      description: 'Modular SQL models, testing, and documentation for analytics engineering.',
      icon: 'layers'
    },
    {
      name: 'Fivetran & Stitch',
      tagline: 'Managed Ingestion',
      description: 'Connector-driven ELT with automated schema evolution and monitoring.',
      icon: 'integration'
    },
    {
      name: 'Talend & Informatica',
      tagline: 'Enterprise ETL',
      description: 'Data quality, lineage, and integration for complex regulatory environments.',
      icon: 'governance'
    },
    {
      name: 'AWS Glue & DataBrew',
      tagline: 'Serverless ETL',
      description: 'Metadata catalogs, job orchestration, and code-free preparation on AWS.',
      icon: 'cloud'
    },
    {
      name: 'Azure Data Factory & Synapse Pipelines',
      tagline: 'Hybrid Integration',
      description: 'Visual pipelines, integration runtimes, and governance within Azure ecosystems.',
      icon: 'cloudUpload'
    },
    {
      name: 'Sigma, Looker & Power BI',
      tagline: 'Analytics Consumption',
      description: 'Self-service dashboards, governed semantic layers, and embedded analytics.',
      icon: 'dashboard'
    }
  ];

  const marqueeRows = [
    technologies.filter((_, index) => index % 2 === 0),
    technologies.filter((_, index) => index % 2 === 1)
  ].filter((row) => row.length);

  const useCases = [
    {
      title: 'Enterprise Data Platforms',
      description: 'Consolidate sources into governed lakehouse foundations with catalogues, lineage, and policy automation.',
      icon: 'dataStack'
    },
    {
      title: 'Customer 360 & Personalisation',
      description: 'Unify customer signals to enable real-time segmentation, recommendations, and marketing automation.',
      icon: 'target'
    },
    {
      title: 'IoT & Telemetry Analytics',
      description: 'Process high-velocity device, sensor, and manufacturing data with streaming analytics and alerting.',
      icon: 'realtime'
    },
    {
      title: 'Finance & Regulatory Reporting',
      description: 'Deliver reconciled financial data marts, audit-ready controls, and compliance dashboards.',
      icon: 'governance'
    }
  ];

  return (
    <motion.div 
      className="service-page data-engineering-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      <section className="service-hero parallax-container" ref={heroRef}>
        <div className="parallax-bg"></div>
        <div className="container">
          <motion.div 
            className="hero-content text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="hero-icon"
              initial={{ scale: 0 }}
              animate={heroInView ? { scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src={dataEngineeringArtwork} 
                alt="Abstract data engineering illustration" 
              />
            </motion.div>
            <h1>Data Engineering</h1>
            <p className="hero-description">
              Build resilient data platforms that fuel analytics, AI, and regulatory requirements. We modernise estates and keep them run-ready with automation, governance, and transparent operational metrics.
            </p>
            <Link to="/contact" className="btn">Build Your Data Infrastructure</Link>
          </motion.div>
        </div>
      </section>

      <section className="what-we-do section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What We Do</h2>
            <p>
              Architects, engineers, and platform operators co-create a roadmap that connects data ingestion to consumption. We blend advisory, implementation, and run services so your teams inherit a governed, automated, and extensible ecosystem.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="features-section section section-alt" ref={featuresRef}>
        <div className="container">
          <div className="grid-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card card"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <IconBadge name={feature.icon} size={72} backgroundOpacity={0.24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="process-section section" ref={processRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Delivery Approach</h2>
            <p>A proven, transparent path to modernised and dependable data infrastructure</p>
          </motion.div>

          <div className="process-timeline">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                className="process-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="process-number">{item.step}</div>
                <div className="process-content">
                  <div className="process-icon">
                    <IconBadge
                      name={item.icon}
                      className="icon-badge--sm"
                      size={58}
                      backgroundOpacity={0.24}
                    />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="use-cases-section section section-alt">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Where We Deliver Impact</h2>
            <p>High-value programmes engineered with CloudRaize data teams</p>
          </motion.div>

          <div className="grid-4">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                className="use-case-item card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
              >
                <div className="use-case-icon">
                  <IconBadge
                    name={useCase.icon}
                    className="icon-badge--sm"
                    size={56}
                    backgroundOpacity={0.24}
                  />
                </div>
                <h3>{useCase.title}</h3>
                <p>{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="technologies-section section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Technologies & Platforms</h2>
            <p>Best-of-breed tools we deploy, integrate, and support</p>
          </motion.div>

          <motion.div
            className="tech-marquee-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {marqueeRows.map((row, rowIndex) => (
              <div
                className={`tech-marquee${rowIndex % 2 === 1 ? ' tech-marquee--reverse' : ''}`}
                key={`dataeng-row-${rowIndex}`}
              >
                <div className="tech-marquee__track">
                  {[...row, ...row].map((tech, index) => (
                    <div className="tech-marquee__card" key={`dataeng-tech-${rowIndex}-${index}-${tech.name}`}>
                      <IconBadge
                        name={tech.icon}
                        className="tech-marquee__badge icon-badge--sm"
                        size={58}
                        backgroundOpacity={0.24}
                      />
                      <div className="tech-marquee__label">
                        <span className="tech-marquee__title">{tech.name}</span>
                        {tech.tagline && (
                          <span className="tech-marquee__subtitle">{tech.tagline}</span>
                        )}
                        {tech.description && (
                          <span className="tech-marquee__description">{tech.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default DataEngineering;
